import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X, Package, Pencil } from 'lucide-react';
import api from '../../services/api';
import { formatMontant } from '../../utils/format';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';

// ============================================================
// Gestion des cartons / lots (migration 050)
// Un carton = composition d'articles + prix de vente propre.
// Couvre : carton identique (12 bouteilles) ET mixte (3 valises).
// ============================================================

const ligneComposantVide = () => ({ variante_id: '', quantite: 1 });

function ModalCarton({ carton, variantes, onClose, onSaved }) {
  const [form, setForm] = useState({
    nom: carton?.nom || '',
    prix_vente_magasin: carton?.prix_vente_magasin || 0,
    prix_vente_livraison: carton?.prix_vente_livraison || 0,
    composants: carton?.composants?.length
      ? carton.composants.map(c => ({ variante_id: c.variante_id, quantite: c.quantite }))
      : [ligneComposantVide()],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inputCls = 'bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-500';
  const libVariante = (v) => `${v.produit_nom}${v.modele ? ` · ${v.modele}` : ''}`;

  const majComp = (i, champ, val) =>
    setForm(f => ({ ...f, composants: f.composants.map((c, idx) => idx === i ? { ...c, [champ]: val } : c) }));
  // Nouveau composant ajouté EN TÊTE → le dernier saisi reste toujours en haut.
  const ajouterComp = () => setForm(f => ({ ...f, composants: [ligneComposantVide(), ...f.composants] }));
  const retirerComp = (i) => setForm(f => ({ ...f, composants: f.composants.filter((_, idx) => idx !== i) }));

  // Somme des prix unitaires des composants (référence pour le prix du carton)
  const sommeUnites = form.composants.reduce((s, c) => {
    const v = variantes.find(x => x.id === c.variante_id);
    return s + (v ? Number(v.prix_vente_magasin || 0) * Number(c.quantite || 0) : 0);
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return setError('Nom du carton requis');
    const comps = form.composants.filter(c => c.variante_id && c.quantite > 0);
    if (comps.length === 0) return setError('Ajoute au moins un article au carton');
    if (!form.prix_vente_magasin || form.prix_vente_magasin <= 0) return setError('Prix du carton requis');
    setError(''); setSaving(true);
    try {
      const payload = {
        nom: form.nom.trim(),
        prix_vente_magasin: parseInt(form.prix_vente_magasin) || 0,
        prix_vente_livraison: parseInt(form.prix_vente_livraison) || 0,
        composants: comps.map(c => ({ variante_id: c.variante_id, quantite: parseInt(c.quantite) })),
      };
      if (carton) await api.put(`/cartons/${carton.id}`, payload);
      else await api.post('/cartons', payload);
      toast.success(carton ? 'Carton modifié' : 'Carton créé');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Package size={16} className="text-yellow-400" /> {carton ? 'Modifier le carton' : 'Nouveau carton / lot'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Nom du conditionnement *</label>
          <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })}
            placeholder="Ex : Tonne fer 10mm, Carton huile 5L, Lot 3 valises" className={`w-full ${inputCls}`} autoFocus />
        </div>

        {/* Composition */}
        <div className="border border-gray-700 rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-300">Contenu (combien d'unités de base)</p>
            <button type="button" onClick={ajouterComp} className="text-yellow-400 text-xs flex items-center gap-1">
              <Plus size={12} /> Article
            </button>
          </div>
          {form.composants.map((c, i) => (
            <div key={i} className="flex flex-col gap-2 sm:grid sm:grid-cols-12 sm:items-center">
              <select value={c.variante_id} onChange={e => majComp(i, 'variante_id', e.target.value)}
                className={`col-span-8 ${inputCls}`}>
                <option value="">— Choisir un article —</option>
                {variantes.map(v => <option key={v.id} value={v.id}>{libVariante(v)}</option>)}
              </select>
              <input type="number" min="1" value={c.quantite}
                onChange={e => majComp(i, 'quantite', e.target.value)}
                className={`col-span-3 text-right ${inputCls}`} title="Nombre d'unités de base" />
              <button type="button" onClick={() => retirerComp(i)}
                className="col-span-1 text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
          <p className="text-[11px] text-gray-500 leading-relaxed">
            Exemples : 1 <b>tonne</b> = 56 × Fer 10mm · 1 <b>carton</b> = 4 × Huile 5L ·
            1 <b>lot</b> = 1 Cabine + 1 Moyenne + 1 Grande. La vente décompte ces unités du stock.
          </p>
        </div>

        {/* Prix */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Prix carton (magasin) *</label>
            <InputMontant value={form.prix_vente_magasin}
              onChange={v => setForm({ ...form, prix_vente_magasin: v })}
              className={`w-full text-right ${inputCls}`} placeholder="0" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Prix carton (livraison)</label>
            <InputMontant value={form.prix_vente_livraison}
              onChange={v => setForm({ ...form, prix_vente_livraison: v })}
              className={`w-full text-right ${inputCls}`} placeholder="optionnel" />
          </div>
        </div>
        {sommeUnites > 0 && (
          <p className="text-xs text-gray-400">
            Référence : {formatMontant(sommeUnites)} si vendu à l'unité.
            {form.prix_vente_magasin > 0 && form.prix_vente_magasin < sommeUnites &&
              <span className="text-emerald-400"> (carton = {formatMontant(sommeUnites - form.prix_vente_magasin)} d'économie pour le client)</span>}
          </p>
        )}

        {error && <p className="text-sm text-red-400 bg-red-900/20 border border-red-700/40 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-gray-800 text-gray-300 rounded-xl text-sm">Annuler</button>
          <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-yellow-500 text-black font-bold rounded-xl text-sm disabled:opacity-50">
            {saving ? '…' : (carton ? 'Enregistrer' : 'Créer le carton')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function GestionCartons({ onClose }) {
  const [cartons, setCartons] = useState([]);
  const [variantes, setVariantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(undefined); // undefined = fermé, null = nouveau, objet = édition

  const charger = () => {
    setLoading(true);
    Promise.all([
      api.get('/cartons').then(r => setCartons(r.data || [])).catch(() => setCartons([])),
      api.get('/produits/variantes').then(r => setVariantes(r.data || [])).catch(() => setVariantes([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { charger(); }, []);

  const supprimer = async (c) => {
    if (!window.confirm(`Désactiver le carton « ${c.nom} » ?`)) return;
    try { await api.delete(`/cartons/${c.id}`); toast.success('Carton désactivé'); charger(); }
    catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Package size={18} className="text-yellow-400" /> Conditionnements (tonne, carton, lot…)</h2>
            <p className="text-xs text-gray-400">Vends groupé à un prix unique : tonne de fer, carton d'huile, lot de valises. Le stock des unités de base est décompté à la vente.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <button onClick={() => setEdit(null)}
          className="w-full mb-4 py-2.5 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
          <Plus size={15} /> Nouveau conditionnement
        </button>

        {loading ? (
          <p className="text-center text-gray-400 py-8">Chargement…</p>
        ) : cartons.length === 0 ? (
          <p className="text-center text-gray-500 py-8 text-sm">Aucun conditionnement. Crée ton premier (tonne, carton, lot…) ci-dessus.</p>
        ) : (
          <div className="space-y-2">
            {cartons.map(c => (
              <div key={c.id} className="bg-gray-800/60 border border-gray-700 rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-medium">{c.nom}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {(c.composants || []).map(co => `${co.quantite}× ${co.produit_nom}${co.modele ? ` (${co.modele})` : ''}`).join(' + ')}
                    </p>
                    <p className="text-xs mt-1">
                      <span className="text-yellow-400 font-bold">{formatMontant(c.prix_vente_magasin)}</span>
                      <span className="text-gray-500"> · {c.stock_disponible} carton(s) formable(s)</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEdit(c)} className="p-1.5 text-blue-400 hover:bg-blue-900/30 rounded" title="Modifier"><Pencil size={13} /></button>
                    <button onClick={() => supprimer(c)} className="p-1.5 text-red-400 hover:bg-red-900/30 rounded" title="Désactiver"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {edit !== undefined && (
          <ModalCarton carton={edit} variantes={variantes}
            onClose={() => setEdit(undefined)}
            onSaved={() => { setEdit(undefined); charger(); }} />
        )}
      </div>
    </div>
  );
}
